import { QueryClient, useMutation } from "@tanstack/react-query"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { createForum } from "../../controllers/forum/forum"
import { useContext, useState } from "react"
import { UserContext } from "../../context/UserContext"
import { 
    Button, 
    Card, 
    CardActions, 
    CardContent, 
    CardHeader, 
    TextField,
    Typography,
    Box,
    Paper,
    Stack,
    Alert,
    Container
} from "@mui/material"
import { 
    Forum as ForumIcon,
    Description as DescriptionIcon,
    Category as CategoryIcon,
    Image as ImageIcon
} from "@mui/icons-material"
import { PostMedia } from "../../components/post/card/PostMedia"
import { useNavigate } from "react-router"
import { CategoryPicker } from "../../components/category.picker"
import { createComment } from "../../controllers/posts/comments"

export const CreateForum = () => {
    const { userDetails } = useContext(UserContext)
    const [media_ids, setMediaIds] = useState([])
    const [imageUrls, setImageUrls] = useState([])
    const [status, setStatus] = useState('')
    const [title, setTitle] = useState('')
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const token = userDetails?.token
    const [category, setCategory] = useState('')
    const navigate = useNavigate()

    const validateForm = () => {
        const newErrors = {}
        
        if (!title.trim()) newErrors.title = 'Topic title is required'
        if (title.trim().length < 5) newErrors.title = 'Title must be at least 5 characters long'
        if (!status.trim()) newErrors.content = 'Topic content is required'
        if (status.trim().length < 10) newErrors.content = 'Content must be at least 10 characters long'
        if (!category) newErrors.category = 'Topic category is required'
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const queryClient = new QueryClient()
    const mutation = useMutation({
        mutationFn: createForum,
        onSuccess: (data) => {
            setIsSubmitting(false)
            queryClient.invalidateQueries({ queryKey: ['createForum'] })
            navigate("/forum/show/" + data.forum._id)
        },
        onError: (error) => {
            setIsSubmitting(false)
            console.error('Error creating forum topic:', error)
        }
    })

    const mutationComment = useMutation({
        mutationFn: createComment,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['createComment'] })
            sendCreateForum(data.id)
        },
        onError: (error) => {
            setIsSubmitting(false)
            console.error('Error creating comment:', error)
        }
    })
    
    const sendCreateForum = (status_id) => {
        if (!validateForm()) return
        
        setIsSubmitting(true)
        mutation.mutate({
            title: title || status, // Use title if available, fallback to status for compatibility
            status: status_id,
            category,
            author: [userDetails?.id],
            token: userDetails?.token
        })
    }
  
    const send_reply = () => {
        if (!validateForm()) return
        
        setIsSubmitting(true)
        mutationComment.mutate({
            body: {
                status,
                visibility: "unlisted",
                media_ids
            },
            token
        })
    }

    return (
        <MainLoggedInLayout>
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Card elevation={3} sx={{ borderRadius: 2 }}>
                    <CardHeader
                        avatar={<ForumIcon color="primary" />}
                        title={
                            <Typography variant="h4" component="h1" fontWeight="bold">
                                Create Forum Topic
                            </Typography>
                        }
                        subheader={
                            <Typography variant="body2" color="text.secondary">
                                {new Date().toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                })}
                            </Typography>
                        }
                        sx={{ pb: 1 }}
                    />
                    
                    <CardContent sx={{ px: 3 }}>
                        <Stack spacing={4}>
                            {/* Topic Information */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DescriptionIcon color="primary" />
                                    Topic Details
                                </Typography>
                                
                                <Stack spacing={3}>
                                    <TextField
                                        fullWidth
                                        label="Topic Title"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        error={!!errors.title}
                                        helperText={errors.title || "Enter a clear and descriptive title for your topic"}
                                        variant="outlined"
                                        placeholder="What would you like to discuss?"
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={6}
                                        label="Topic Content"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        error={!!errors.content}
                                        helperText={errors.content || "Share your thoughts, ask questions, or start a discussion"}
                                        variant="outlined"
                                        placeholder="Write your forum post content here. Be descriptive and engaging to encourage discussion..."
                                    />
                                </Stack>
                            </Paper>

                            {/* Category */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CategoryIcon color="primary" />
                                    Category
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Choose a category that best fits your topic to help others find it
                                </Typography>
                                
                                <Box sx={{ mt: 2 }}>
                                    <CategoryPicker 
                                        category={category} 
                                        setCategory={setCategory} 
                                    />
                                    {errors.category && (
                                        <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>
                                            {errors.category}
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>

                            {/* Media Attachments */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ImageIcon color="primary" />
                                    Attachments (Optional)
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Add images to support your discussion topic
                                </Typography>
                                
                                <Box sx={{ mt: 2 }}>
                                    <PostMedia 
                                        imageUrls={imageUrls} 
                                        setImageUrls={setImageUrls} 
                                        media_ids={media_ids} 
                                        setMediaIds={setMediaIds} 
                                        token={token} 
                                    />
                                </Box>
                            </Paper>

                            {/* Error Alert */}
                            {Object.keys(errors).length > 0 && (
                                <Alert severity="error" sx={{ borderRadius: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Please fix the following errors:
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                        {Object.values(errors).map((error, index) => (
                                            <li key={index}>
                                                <Typography variant="body2">{error}</Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}

                            {/* Success Preview */}
                            {title && status && category && (
                                <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                                    <Typography variant="h6" gutterBottom>
                                        💬 Topic Preview
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Your forum topic "{title}" is ready to be posted! 
                                        Other members will be able to read and respond to your discussion.
                                    </Typography>
                                </Paper>
                            )}

                            {/* Forum Guidelines */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                                <Typography variant="h6" gutterBottom>
                                    📋 Forum Guidelines
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    • Be respectful and constructive in your discussions<br/>
                                    • Use clear, descriptive titles to help others find your topic<br/>
                                    • Choose the most appropriate category for better organization<br/>
                                    • Add relevant images or media to enhance your post
                                </Typography>
                            </Paper>
                        </Stack>
                    </CardContent>
                    
                    <CardActions sx={{ p: 3, pt: 0 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={send_reply}
                            disabled={isSubmitting}
                            sx={{
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: 3,
                                '&:hover': {
                                    boxShadow: 6,
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            {isSubmitting ? 'Creating Topic...' : '💬 Create Topic'}
                        </Button>
                    </CardActions>
                </Card>
            </Container>
        </MainLoggedInLayout>
    )
}